import { ElementRef, Injectable } from '@angular/core';
import { Location, DecimalPipe, DatePipe } from '@angular/common';
import moment from 'moment';
import * as _ from 'underscore';
import objectMappers from './mapper.json';
import iotemeterMapper from 'src/app/shared/services/iotemeterMapper.json';
import { CommonDataService } from './common-data.service';
import validationRulesModel from '../../../../../iotah-common/validations/validation.json';
import validationRulesModelChargLink from '../../../../../iotah-common/validations/validation-cl.json';
import validationRulesModelIM from '../../../../../iotah-common/validations/validation-IM.json';
import { ValidationService } from './validation.service';
import { TranslateService } from '@ngx-translate/core';
import { isDevMode } from '@angular/core';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import { DomainsService } from './domains.service';
import { PageTitleService } from './page-title.service';
import { DeviceFactory } from '../../home/site-dashboard/device/device-factory/device-factory';
import { Router } from '@angular/router';
import { ColumnsConfig } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { decodeLogLine } from 'src/app/tools/logDecode';

/**
 * Where a displayed timestamp gets its time zone from. Pass one to
 * CommonService.getTimeSourceTooltip to get the hover text for a column.
 */
export enum TimeSource {
	/** A true UTC value, rendered with .utc() and never shifted. */
	Utc = 'utc',
	/**
	 * The device's own clock. Values that come off the device (event time, glitch time,
	 * debug record time) are already device-local, and values shifted with
	 * getZoneTimestampFromUTC are put onto that same clock deliberately.
	 */
	Device = 'device',
	/** Rendered in the browser's zone, so each viewer sees their own local time. */
	PcTime = 'pc_time',
}

@Injectable({
	providedIn: 'root'
})
export class CommonService {

	constructor(
		private commonData: CommonDataService,
		private location: Location,
		private translate: TranslateService,
		private validationService: ValidationService,
		private datePipe: DatePipe,
		private domainsService: DomainsService,
		private pageTitleService: PageTitleService,
		private router: Router,
	) {
		this.convertHourToSeconds = this.convertHourToSeconds.bind(this);
		this.durationFormatter = this.durationFormatter.bind(this);
	}

	readonly FLOAT_VALUE_LIMIT = 3.4E+38;

	readonly NO_READING = 'N/A';

	hasNoReading(value: any): boolean {
		return Number(value) > this.FLOAT_VALUE_LIMIT;
	}

	readonly utcZoneId = 0;
	
	alphabetCharacters = "abcdefghijklmnopqrstuvwxyz".split('');
	providedFilterParams: any;
	monthsText = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

	isMobileScreen: boolean = window.innerWidth < 767;
	isTabletOrMobileScreen: boolean = window.innerWidth <= this.commonData.tabletWidth;

	powerStudiesDevicesSiteName: string = 'Power Studies Devices';
	powerStudyDevicesSiteId: number = 99999;

	CBL_CUSTOMER_ID: number = 728;
	CBL_INVENTORY_SITE_ID: number = 2005;
	CBL_ENTERPRISE_ID: number = 428;

	QV_SNAPSHOT_SOURCE = {
		1: 'define',
		2: 'detailedQV'
	};

	DEVICES_TYPE = {
		'Truklink': 'Truklink',
		'ChargLink': 'ChargLink',
		'Truklink_ps': 'Truklink_ps',
		'ChargLink_brm': 'ChargLink BRM',
		'Battlink': 'BattLink',
		'LithiumBattlink': 'Lithium BATTlink',
		'BattlinkII': 'BATTlink II',
		'ioTEmeterSp1': 'IoTEmeter SP1',
		'ioTEmeterSp3': 'IoTEmeter SP3',
		'ioTEmeter': 'IoTEmeter Pro',
		'ioTEmeterEdge': 'IoTEmeter Edge'
	};

	daysText  = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
	workingDays: Array<{label: string, id: number}> = [
		{ label: "Monday", id: 0 },
		{ label: "Tuesday", id: 1 },
		{ label: "Wednesday", id: 2 },
		{ label: "Thursday", id: 3 },
		{ label: "Friday", id: 4 },
		{ label: "Saturday", id: 5 },
		{ label: "Sunday", id: 6 }
	];

	deviceMappers = {
		objectMappers,
		iotemeterMapper,
	};

	usaStates = {
		oregon: 'Oregon',
		california: 'California',
	};

	getObjectMapper(deviceType: number) {
		if (!deviceType)
			return this.deviceMappers.objectMappers;

		const deviceConfig = DeviceFactory.createDevice(deviceType).config;
		const mapperOverrideFile = deviceConfig.mapperOverrideFile;

		let objectMapperFields = this.deviceMappers.objectMappers;

		if (mapperOverrideFile)
			objectMapperFields = this.deviceMappers[mapperOverrideFile];

		return objectMapperFields;
	}

	compress(data, type, deviceType?: number) {
		const objectMappers = this.getObjectMapper(deviceType);

		let compressedData = {};
		let mapped = objectMappers[type];
		for (let field in data) {
			if(mapped[field])
				compressedData[mapped[field]] = data[field];
			else
				compressedData[field] = data[field];
		}
		return compressedData;
	}

	compare = (a: number | string, b: number | string, isAsc: boolean) => {
		return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
	}

	showUserTimeZoneReference() {
		const userTimeZone = moment().utcOffset();
		return moment().utcOffset(userTimeZone).format('UTCZ');
	}

	/**
	 * Sets the time-source hover text on each time column named in sources. Columns are patched
	 * in place, so the caller keeps whatever column order and visibility the user has chosen.
	 *
	 * headerTooltip shows the text on hover over the column header. Unlike hasTooltip it adds no
	 * question-mark icon, which is why sct-custom-table gained the field for this.
	 */
	applyTimeSourceTooltips(columns: ColumnsConfig[], sources: Record<string, TimeSource>) {
		for (const column of columns) {
			const source = sources[column.key];
			if (!source)
				continue;

			column.headerTooltip = this.getTimeSourceTooltip(source);
		}
	}

	/**
	 * Returns the short hover label that says where a displayed timestamp gets its time zone
	 * from. Call it per column, not per row - the text is the same for a whole column.
	 */
	getTimeSourceTooltip(source: TimeSource): string {
		switch (source) {
			case TimeSource.Device:
				return this.translate.instant('tooltip.time_source_device');

			case TimeSource.PcTime:
				return this.translate.instant('tooltip.time_source_pc');

			default:
				return this.translate.instant('tooltip.time_source_utc');
		}
	}

	empty(data) {
		if(typeof(data) == 'number' || typeof(data) == 'boolean')
		{
			return false;
		}
		if(typeof(data) == 'undefined' || data === null)
		{
			return true;
		}
		if(typeof(data.length) != 'undefined')
		{
			return data.length == 0;
		}
		var count = 0;
		for(var i in data)
		{
			if(data.hasOwnProperty(i))
				count ++;
		}
		return count == 0;
	}

	getZoneName(zoneId, defaults = 0) : string {
		let zone = this.commonData.TimeZonesMenu[zoneId - 1];

		if(!zone) {
			if(!defaults)
				defaults = 14;
			zone = this.commonData.TimeZonesMenu[defaults];
		}
		return zone.display_name;
	}

	getUTCTimestampFromZone(zoneId, unixTimestamp) {
		var zone = this.commonData.TimeZonesMenu[zoneId - 1];

		if (!zone || zone.id == 0)
			return unixTimestamp;

		unixTimestamp = (unixTimestamp - zone.base_utc);

		for (let i = 0; i < 50; i++) {

			if (unixTimestamp >= zone.changes_time[i])
				unixTimestamp = (unixTimestamp + ((i % 2 == 0) ? -1 : 1) * zone.changes_value);
		}

		return unixTimestamp;
	}

	getZoneTimestampFromUTC(zoneId, unixTimestamp) {

		var zone = this.commonData.TimeZonesMenu[zoneId - 1];

		if (!zone || zone.id == 0)
			return unixTimestamp;

		unixTimestamp = (unixTimestamp + zone.base_utc);

		//DaylightSaving
		for (let i = 0; i < 50; i++) {

			if (unixTimestamp >= zone.changes_time[i])
				unixTimestamp = (unixTimestamp + ((i % 2 == 0) ? 1 : -1) * zone.changes_value);
		}

		return unixTimestamp;
	}

	getDateFormattedFromUnixTimeStamp(timeStamp, format = 'default') {
		if(timeStamp!=+timeStamp) return "";
		var LEAPOCH =(946684800 + 86400*(31+29));
		var DAYS_PER_400Y =(365*400 + 97);
		var DAYS_PER_100Y =(365*100 + 24);
		var DAYS_PER_4Y  = (365*4   + 1);
		var remdays, remsecs, remyears;
		var qc_cycles, c_cycles, q_cycles;
		var years, months;
		var wday, yday, leap;
		var days_in_month = [31,30,31,30,31,31,30,31,30,31,31,29];
		var secs = timeStamp - LEAPOCH;
		var days = Math.floor(secs / 86400);
		remsecs = secs % 86400;
		if (remsecs < 0) {
			remsecs += 86400;
			days--;
		}

		wday = (3+days)%7;
		if (wday < 0) wday += 7;

		qc_cycles = Math.floor(days / DAYS_PER_400Y);
		remdays = days % DAYS_PER_400Y;
		if (remdays < 0) {
			remdays += DAYS_PER_400Y;
			qc_cycles--;
		}

		c_cycles = Math.floor(remdays / DAYS_PER_100Y);
		if (c_cycles == 4) c_cycles--;
		remdays -= c_cycles * DAYS_PER_100Y;

		q_cycles = Math.floor(remdays / DAYS_PER_4Y);
		if (q_cycles == 25) q_cycles--;
		remdays -= q_cycles * DAYS_PER_4Y;

		remyears = Math.floor(remdays / 365);
		if (remyears == 4) remyears--;
		remdays -= remyears * 365;

		leap = !remyears && (q_cycles || !c_cycles);
		yday = remdays + 31 + 28 + leap;
		if (yday >= 365+leap) yday -= 365+leap;

		years = remyears + 4*q_cycles + 100*c_cycles + 400*qc_cycles;

		for (months=0; days_in_month[months] <= remdays; months++)
			remdays -= days_in_month[months];

		years+=2000;
		months+=3;//1 = January
		if(months > 12) {
			months -=12;
			years++;
		}
		function zeroPad(num, places) {
			var zero = places - num.toString().length + 1;
			return Array(+(zero > 0 && zero)).join("0") + num;
		}

		function getHourFromSeconds(timeInSeconds) {
			var hours = Math.floor(timeInSeconds / 3600);
			var minutes = Math.floor((timeInSeconds % 3600) / 60);
			var period;
			if(hours >= 12 && hours != 24) {

				if(hours != 12)
					hours -= 12;
				 period = 'PM';
			} else {

				if(hours == 0 || hours == 24)
					hours = 12;
				period = 'AM';
			}

			return {
				'hours':	hours,
				'minutes':	minutes,
				'period':	period
			};
		}

		var monthsText	= ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

		var str;
		switch (format) {
			case "date":
				str = zeroPad(months,2) +"/" + zeroPad(remdays + 1,2) +  "/" + years.toString();
			break;
			case "LCDprojection":
				var time = getHourFromSeconds(remsecs);
				str = zeroPad(time.hours, 2) + ":" + zeroPad(time.minutes, 2) + ' ' + time.period + ' - ' + this.daysText[wday] + ' ' + monthsText[months-1] + '/' + (remdays + 1);
			break;
			case "12h":
				let hours: any = Math.floor(remsecs / 3600);
				let amPM = 'PM';
				if(hours < 12) {
					amPM = 'AM';
					if(hours == 0) {
						hours = 12;
					}
				} else {
					hours -= 12;
				}
				hours = zeroPad(hours, 2);
				str = zeroPad(months,2) +"/" + zeroPad(remdays + 1,2) +  "/" + years.toString() + " " + hours + ":" + zeroPad(Math.floor(remsecs / 60 % 60),2) + ":" + zeroPad(Math.floor(remsecs % 60),2) + " "+ amPM;
			break;
			default:
				str = zeroPad(months,2) +"/" + zeroPad(remdays + 1,2) +  "/" + years.toString() + " " + zeroPad(Math.floor(remsecs / 3600),2) + ":" + zeroPad(Math.floor(remsecs / 60 % 60),2) + ":" + zeroPad(Math.floor(remsecs % 60),2);
			break;
		}
		return str;
	}

	decompress(data, type, deviceType: number = 0) {
		const objectMappers = this.getObjectMapper(deviceType);

		let decompressedData: any = {};
		let mapped = this.swapObjectKeyAndValue(objectMappers[type]);
		for (let field in data) {
			let mappedKey = mapped[field];
			if(mappedKey) {
				let special = {"live_event": 'events', "live_rt": 'rt'};
				let specialType = special[mappedKey];
				if(specialType)
					decompressedData[mappedKey] = this.decompress(data[field], specialType, deviceType);
				else
					decompressedData[mappedKey] = data[field];
			} else
				decompressedData[field] = data[field];
		}
		return decompressedData;
	}

	genRandomString(len) {
		var text = "";
		var possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

		for (var i = 0; i < len; i++)
			text += possible.charAt(Math.floor(Math.random() * possible.length));

		return text;
	}

	getDomainName() {
		const angularRoute = this.location.path();
		const url = window.location.href;
		const domain = url.replace(angularRoute, '');
		return domain;
	}

	getCaptchaUrl() {
		let captchaKey = this.genRandomString(10);
		return {
			'url': this.getDomainName()+"/user/captchaImage/"+captchaKey+"/0",
			'key': captchaKey
		};
	}

	revertPermissionValue(permissionsObj){
		let mapper = {
			0: "noAccess",
			1: "read",
			2: "write",
		};
		let res = {};
		for (let key in permissionsObj) {
			if(['rt', 'events', 'quick_view', 'mobile_master', 'mobile_network', 'online_network', 'router_network'].includes(key))
				res[key] = this.revertPermissionValue(permissionsObj[key]);
			else
				res[key] = mapper[permissionsObj[key]] || permissionsObj[key];
		}
		return res;
	}

	swapObjectKeyAndValue(sourceObj) {
		return Object.keys(sourceObj).reduce((obj, key) => (obj[sourceObj[key]] = key, obj), {});
	}

	timeFormat(input, options: any = {timeSystem: 24, limitToOneDay: false}) {
		var timeSystem		= options.timeSystem || 24;
		var limitToOneDay	= options.limitToOneDay || false;
		if (limitToOneDay && input >=(1*24*60*60)) {
			return '24:00';
		}

		var format = "HH:mm";
		if (timeSystem == 12) {
			format = "hh:mm A";
		}

		return moment("2015-01-01").startOf('day').seconds(input).format(format);
	}

	longTimeFormat(input: number): string {
		const duration = moment.duration(input, 'seconds');
		const hours = Math.floor(duration.asHours());
		const minutes = duration.minutes();

		return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
	}

	getHoursFromSeconds(input: number): string {
		const duration = moment.duration(input, 'seconds');
		return `${Math.floor(duration.asHours())}`;
	}

	arrayCompare(arr1, arr2) {
		return arr1.length == arr2.length && _.difference(arr1, arr2).length == 0;
	}

	getDropDownListData(type, options: any = {}) {
		let DayOfWeek = {
			'Sunday': 0,
			'Monday': 1,
			'Tuesday': 2,
			'Wednesday': 3,
			'Thursday': 4,
			'Friday': 5,
			'Saturday': 6
		};
		var dataList = [];
		var i, j;
		switch(type) {
			case "number":
				var num = options.start;
				for(i=0; i<=(options.end-options.start)/options.step; i++) {
					dataList.push(num);
					num += options.step;
				}
			break;
			case "clock":
				var hour, minute: any = "";
				for(j=options.start; j<=options.end; j++) {
					hour = j;
					if (j < 10) {
						hour = "0"+j;
					}
					for(i=0; i<60/options.step; i++) {

						if(j == options.start && options.ignoreFirst) {
							options.ignoreFirst--;
							continue;
						}

						minute = i * options.step;
						if (minute < 10) {
							minute = "0"+minute;
						}
						dataList.push(hour+":"+minute);
						if (j == options.end) {
							break;
						}
					}
				}
			break;
			case "week":
				for(var day in DayOfWeek) {
					if(options && options.days && options.days.indexOf(day) == -1)
						continue;
					dataList.push({id:DayOfWeek[day], text:day});
				}
			break;
		}
		return dataList;
	}

	secondsToDaysHoursMinutesAndSeconds(seconds) {
		let days	= 0,
			text	= '',
			value	= seconds;

		if(value >= 86400){
			days = Math.floor(value / 86400);
			value -= (days * 86400);
		}
		if(days)
			text += days+' '+ (days == 1 ? this.translate.instant('time.day') : this.translate.instant('time.days'));
		if(value)
			text += ' '+moment("2015-01-01").startOf('day').seconds(value).format('HH:mm:ss');
		return text;
	}

	secondsToElapsedTime(disconnectTime, options?) {
		options = options || {};
		var timespanObj = this.convertHourToSeconds(disconnectTime, 0, true, 0, options);

		var timespanTextArr = [];
		var timespanText	= "";
		if(timespanObj['day'] > 0)
			timespanTextArr.push(this.singularOrPlural(timespanObj['day'], "day"));
		if(timespanObj['hour'] > 0)
			timespanTextArr.push(this.singularOrPlural(timespanObj['hour'], "hour"));

		if(timespanTextArr.length > 0)
			timespanText = timespanTextArr.join(" and ");
		else if(timespanObj['min'] > 0)
			timespanText = this.singularOrPlural(timespanObj['min'], "min");

		return timespanText;
	}

	convertHourToSeconds(hour, min, viseVersa = false, sec = 0, options?) {
		options = options || {};
		let seconds: number = 0;
		if(viseVersa) {
			seconds			= +hour;
			let days		= (options.hoursOnly) ? 0 : Math.floor(seconds / 86400);
			let hourTime	= Math.floor((seconds - (days * 24 * 3600)) / 3600);
			let minTime		= Math.floor((seconds - (hourTime * 3600) - (days * 24 * 3600)) / 60);
			return ({day: days, hour: hourTime, min: minTime});
		} else {
			//Converts hour point in time to seconds passed
			//Ex: 01:55 -> 1 * 3600 + 55 * 60 = 6900 seconds
			var hourSeconds = hour * 3600;
			var minSeconds = min * 60;
			seconds = (+sec) || 0;
			return hourSeconds + minSeconds + seconds;
		}
	}

	getRemainingPeriod(date, span) {
		let expiry = moment(date).add(span, 'years');
		let years = expiry.diff(moment(), 'years');
			expiry.subtract(years, 'years');
		let months = expiry.diff(moment(), 'months');
			expiry.subtract(months, 'months');
		let days = expiry.diff(moment(), 'days');
		let vals = [];
		if(years > 0)
			vals.push(years + ' ' + this.translate.instant('g.years_value'));
		if(months > 0)
			vals.push(months + ' ' + this.translate.instant('g.months_value'));
		if(days > 0)
			vals.push(days + ' ' + this.translate.instant('g.days_value'));
		return vals.join(', ');
	}

	singularOrPlural(val, text, concat = null) {
		concat = concat == null ? true : false;
		if(val == 1) {
			return concat ? val + ' ' + text : text;
		}
		return concat ? val + ' ' + text + 's' : text + 's';
	}

	validateDeviceSettings(data){

		let ref				= this;
		let invalidFields	= [];
		var networksFields	= ['mobile_master', 'mobile_network', 'router_network', 'online_network'];

		let validationRules = this.getValidationRules();

		for(let field in data) {
			let rules = validationRules[field] || [];

			if(networksFields.indexOf(field) != -1) {

				for(let subField in data[field]) {
					if(invalidFields.indexOf(subField) == -1) {

						rules = validationRules[field][subField] || [];
						rules.forEach(function(_rule) {

							var isValid = ref.validationService.dataValidator(_rule.type, _rule, data[field][subField]);
							if(!isValid) {
								if(networksFields.indexOf(field) != -1){
									if(invalidFields.indexOf(field+'.'+subField) == -1)
										invalidFields.push(field+'.'+subField); // master.ssid or mixed_access.priority
								} else
									invalidFields.push(subField);
							}
						}, this);
					}
				}
			} else if(invalidFields.indexOf(field) == -1) {
				rules.forEach(function(rule) {

					var isValid = ref.validationService.dataValidator(rule.type, rule, data[field]);
					if(!isValid)
						invalidFields.push(field);
				}, this);
			}
		}
		return invalidFields;
	}

	getValidationRules(isIMDevice=false){
		let projectRules = {...validationRulesModel};
		// @todo: Update this line to override all objects insted of device only
		if(this.domainsService.isChargLink)
			projectRules = {...validationRulesModel, device: {...validationRulesModel.device, ...validationRulesModelChargLink.device} };
		
		if (isIMDevice)
			projectRules = {...projectRules, device: {...projectRules.device, ...validationRulesModelIM.device } };

		let rules: any = projectRules.device;
		rules.mobile_master		= this.mergeObjects(projectRules.app_network_empty_pass);
		rules.mobile_network	= this.mergeObjects(projectRules.app_network_empty_pass);
		rules.router_network	= this.mergeObjects(projectRules.app_network_empty_pass);
		rules.online_network	= this.mergeObjects(projectRules.app_network_empty_pass);

		let minInstallationDate	= moment().subtract(5, 'years').utc().unix();
		let maxInstallationDate	= moment().endOf('day').utc().unix();
		rules.installation_date	= [{type: 'integer', min: minInstallationDate, max: maxInstallationDate}];
		rules.hw_version		= [{"type": "regex", "patt": /^([a-zA-Z ]{1,2})$/}];
		rules.serial_number		= [{"type": "regex", "patt": /^(T00(0[1-9]|10|11|12)[0-9][0-9]\d{5})$/}];
		rules.truck_manufacturing_year = [{type: 'integer', min: 2020, max: moment().year()}];
		rules.truck_manufacturing_month = [{type: 'integer', min: 1, max: 12}];

		return rules;
	}

	mergeObjects(obj1, obj2={}, obj3={}) {

		obj3 = obj3 || {};
		var resultObj = {};

		for(let key in obj1) {
			resultObj[key] = obj1[key];
		}

		for(let key in obj2) {
			resultObj[key] = obj2[key];
		}

		for(let key in obj3) {
			resultObj[key] = obj3[key];
		}

		return resultObj;
	}

	getCookie(name) {

		var value	= "; " + document.cookie;
		var parts	= value.split("; " + name + "=");

		if(parts.length == 2)
			return parts.pop().split(";").shift();

		return "";
	}

	lpad(str, length, padString = '0') {
		while (str.length < length)
			str = padString + str;
		return str;
	}

	fahToCel(val, reverse = false) {

		if(reverse)
			return Math.round(val * 1.8 + 32);

		return parseFloat(((val - 32 ) / 1.8).toFixed(1));
	}

	tempUnitSymbol(unit: number): string {
		// SiteTemperatureUnit: 0 = Fahrenheit (default), 1 = Celsius
		return unit == 1 ? '°C' : '°F';
	}

	formatSensorTemp(celsius: number, unit: number): number {
		return unit == 1 ? celsius : this.fahToCel(celsius, true);
	}

	printContent(content) {
		let style = '';
		if (isDevMode()) {
			style = '<script type="text/javascript" src="styles.js"></script>';
		} else {
			let links = document.getElementsByTagName('link');
			for (let i = 0; i < links.length; i++) {
			 	let elem = links[i];
				if(/styles./.test(elem.href))
					style = '<link href="' + elem.href + '" rel="stylesheet"/>' ;
			}
		}

		let wnd = window.open('','');
		let doc = wnd.document;
		doc.open();

		doc.write([
			'<!DOCTYPE html>',
			'<html>',
			'<head>',
			style,
			'</head>',
			'<body>',
			content,
			'<script>setTimeout(function() { window.print();window.close(); } , 1000);</script>',
			'</body>'
		].join(""));
		wnd.document.close();
	}

	formatTotalDeviceNumbers(value) {
		let result, unit = '';
		let decimalPipe: DecimalPipe = new DecimalPipe("en-US");
		if (value < 1000) {
			result = value;

		} else if (value >= 1000 && value < 1000000) {
			result = decimalPipe.transform(value / 1000, '1.1-1');
			unit = "K";

		} else if (value >= 1000000 && value < 1000000000) {
			unit = "M";
			result = decimalPipe.transform(value / 1000000, '1.1-1');

		} else {
			unit = "G";
			result = decimalPipe.transform(value / 1000000000, '1.1-1');
		}
		return {
			result: result,
			unit: unit
		}
	}

	convertTextToDate(text) {
		const parts = text.split(' ');
		const timeValues = {
			hours: 0,
			minutes: 0,
			seconds: 0
		};

		for (let i = 0; i < parts.length; i += 2) {
			const value = parseInt(parts[i]);
			const unit = parts[i + 1]?.toLowerCase();

			switch (unit) {
				case 'days':
					timeValues.hours += value * 24; // Convert days to hours
					break;
				case 'hours':
					timeValues.hours += value;
					break;
				case 'minutes':
					timeValues.minutes += value;
					break;
				case 'seconds':
					timeValues.seconds += value;
					break;
			}
		}

		return [timeValues.hours, timeValues.minutes, timeValues.seconds];
	}

	durationFormatter({filter: filterOption, value, filterText}) {
		if(!filterText)
			return true;

		const textTime = /(\d+)\s*(Days?|Hours?|Minutes?)/gi;
		const isTextDate = textTime.test(filterText);

		let filterTextSplitted	= isTextDate? this.convertTextToDate(filterText) : filterText.split(':');
		let filterValue: any = this.convertHourToSeconds(filterTextSplitted[0], filterTextSplitted[1] || 0, false, filterTextSplitted[2] || 0);
		let returnVal = false;
		let rowValue = value - (value % 60);
		switch (filterOption) {
			case 'lessThan':
				returnVal = rowValue < filterValue;
			break;
			case 'greaterThan':
				returnVal = rowValue > filterValue;
			break;
			default:
				returnVal = rowValue == filterValue;
			break;
		}
		return returnVal;
	}

	getTooltipKey(area, userHasNOCaccess) {
		let key;
		if(userHasNOCaccess) {
			let tempKey = area;
			tempKey += '_noc';
			if(this.translate.instant('tooltip.'+tempKey) != 'tooltip.'+tempKey)
				key = tempKey;
		}
		if(!key) {
			if(this.translate.instant('tooltip.'+area) != 'tooltip.'+area)
				key = area;
		}
		return key;
	}

	getCleanFileName(name) {
		return name.replace(/[/\\?%*:|"<>]/g, '_');
	}

	exportQuartersData(columns, data, fileName) {
		let alphabits = this.alphabetCharacters;

		let columnNames = _.pluck(columns, 'name');
		let fields = _.pluck(columns, 'key');

		let headerObj = {};
		columnNames.forEach((column, colIdx) => {
			let colId = alphabits[colIdx];
			headerObj[colId] = column;
		});

		let dataObj = [];
		for(let quarter in data) {
			dataObj.push({a: quarter}, headerObj);
			for(let qData of data[quarter]) {
				let vals = {};
				fields.forEach((field, idx) => {
					let colId = alphabits[idx];
					vals[colId] = [null, undefined].includes(qData[field]) ? '-' : qData[field];
				});
				dataObj.push(vals);
			}
			dataObj.push({});
		}
		new AngularCsv(dataObj, fileName);
	}

	exportMonthsData(columns, data, fileName, hasYear = true) {
		let alphabits = this.alphabetCharacters;

		let columnNames = _.pluck(columns, 'name');
		let fields = _.pluck(columns, 'key');

		let headerObj = {};
		columnNames.forEach((column, colIdx) => {
			let colId = alphabits[colIdx];
			headerObj[colId] = column;
		});

		if(!hasYear) {
			let dataObj = this.parseMonthsData(data, '', headerObj, fields);
			new AngularCsv(dataObj, fileName);
			return;
		}

		let dataObj = [];
		for(let year in data) {
			dataObj = [...dataObj, ...this.parseMonthsData(data[year], year, headerObj, fields)];
		}
		new AngularCsv(dataObj, fileName);
	}

	parseMonthsData(data, year, headerObj, fields) {
		const alphabits = this.alphabetCharacters;
		const dataObj = [];
		for(let month in data) {
			dataObj.push({a: `${moment(month, 'MM').format('MMM')} ${year}`}, headerObj);
			for(let mData of data[month]) {
				let vals = {};
				fields.forEach((field, idx) => {
					let colId = alphabits[idx];
					vals[colId] = mData[field] !== undefined ? mData[field] : 'N/A';
				});
				dataObj.push(vals);
			}
			dataObj.push({});
		}
		return dataObj;
	}

	exportTableToCsv(columnNames, elements, fileName) {
		let alphabits = this.alphabetCharacters;
		let CSVobj = [];

		let columnNamesObj = {};
		let headerObj = {};

		columnNames.forEach((column, colIdx) => {
			columnNamesObj[column.key] = column;
			let colId = alphabits[colIdx];
			columnNamesObj[column.key].colId = colId;

			if(column.key != 'options')
				headerObj[colId] = this.translate.instant(column.name);
		});

		CSVobj.push(headerObj);

		elements.forEach((element, rowIdx) => {

			let currRow = {};

			for(let key in columnNamesObj) {

				if(key == 'options')
					continue;

				let column = columnNamesObj[key]
				let value = element[column.key];

				switch(column.type) {
					case "json":
						value = JSON.stringify(value);
					break;

					case "dateTime":
						value = this.datePipe.transform(value, 'MM/dd/yyyy hh:mm:ss a');
					break;
				}

				if(column.key == 'serial_number')
					value = '*'+value;

				currRow[column.colId] = value;
			}

			CSVobj.push(currRow);
		});

		new AngularCsv(CSVobj, fileName);
	}

	exportToCsv(columnNames: string[], data: any, fileName: string) {
		const dataObj = [columnNames].slice();

		data = data.slice();
		data.forEach((column: any) => {
			const vals: any = {};
			column.forEach((col: any, idx: any) => {
				const colId = columnNames[idx];
				vals[colId] = col;
			});
			dataObj.push(vals);
		});

		new AngularCsv(dataObj, fileName);
	}

	lbToMt(value) {
		return value * 0.000453592;
	}

	extractKeysFromObjects(keysArr: string[], objectsArr: any[], unique: boolean = false, integers: boolean = false) {
		const items = [];
		const itemsValues = [];
		objectsArr.forEach(function(obj) {
			const tempObject = {};
			keysArr.forEach(function(key) {
				let val = obj[key];
				if(integers)
					val = +obj[key];

				if(val || val == 0) {
					if(unique) {
						if(itemsValues.indexOf(val) == -1)
							itemsValues.push(val);
					} else {
						itemsValues.push(val);
					}
					tempObject[key] = val;
				}
			});
			items.push(tempObject);
		});
		return items;
	}

	getUnixDateByUTCZone(date) {
		const fromDateRange = new Date(new Date(date).setHours(0, 0, 0, 0));
		const zoneDiff = new Date().getTimezoneOffset() * -1;
		return Math.round(new Date(new Date(fromDateRange).getTime() + (zoneDiff * 60 * 1000)).getTime() / 1000);
	}

	toFixedWithoutRounding(num: number, fixed: number): number {
		return Math.floor(Math.pow(10, fixed) * num) / Math.pow(10, fixed);
	}

	nowTime() {
		return moment().utc().unix();
	}

	ip2int(ip) {
		return (
			ip.split('.').reduce((ipInt, octet) => {
				return (ipInt << 8) + parseInt(octet, 10);
			}, 0) >>> 0 // to make it unsigned int
		);
	}

	int2ip(ipInt) {
		return (
			(ipInt >>> 24) +
			'.' +
			((ipInt >> 16) & 255) +
			'.' +
			((ipInt >> 8) & 255) +
			'.' +
			(ipInt & 255)
		);
	}

	getFormattedTimeByZone(time: number, zoneId: number, format = 'MM/dd/yyyy hh:mm:ss a') {
		const date = this.getDateFormattedFromUnixTimeStamp(this.getZoneTimestampFromUTC(zoneId, time));
		return this.datePipe.transform(date, format);
	}

	exportTotalUsageWidget(data, translationPath = "", options: any = {}) {
		let alphabits = this.alphabetCharacters;
		let columnNames = [
			this.translate.instant('g.date'),
			this.translate.instant('g.time'),
			this.translate.instant('g.kw'),
			this.translate.instant('g.amp')
			];

		let headerObj = {};
		columnNames.forEach((column, colIdx) => {
			let colId = alphabits[colIdx];
			headerObj[colId] = column;
		});
		let header = [headerObj];
		const fromDate	= moment(data.startDate).format('YYYY-MM-DD');
		const toDate	= moment(data.endDate).format('YYYY-MM-DD');

		let fileName = this.getCleanFileName(data.siteName) +
			'_' + fromDate + '_' + toDate
			+ '_' + this.translate.instant(translationPath + 'site_performance.total_truck_usage');

		if (options.fileName)
			fileName = options.fileName;

		let colLength = Object.keys(headerObj).length;
		let dataObj = header.slice();

		let chartData = data.usageAmpData.slice();  // AMP data
		const allWattData = _.map(data.usageWattData, (subarray) => subarray[1]);  // watt data

		 // Insert watt data into amp data array
		_.forEach(chartData, (subarray, index) => {
			const wattData = allWattData[index];
			const date = subarray[0];
			subarray[0] = moment(subarray[0]).format('YYYY-MM-DD');
			const time = moment(date).format('HH:mm:ss');
			subarray.splice(1,0, time);
			subarray.splice(2, 0, wattData);
			});

		chartData.forEach((column) => {
			let vals = {};
			column.forEach((col, idx) => {
				if(idx >= colLength)
					return;
				let colId = alphabits[idx];
				vals[colId] = col;
			});
			dataObj.push(vals);
		});
		new AngularCsv(dataObj, fileName);
	}

	getUnixTimezoneByLocaltime(timestamp: number) {
		return Math.floor(new Date().getTimezoneOffset() * 60 + timestamp);
	}

	getMotiveEnergyCustomerData() {
		const isProduction = this.domainsService.isProductionDomain;
		const isStaging = this.domainsService.isStagingDomain;

		const motiveEnergyEnterprise = isProduction ? 36 : (isStaging ? 36 : 77);
		const motiveEnergyCustomerId = isProduction ? 337 : (isStaging ? 204 : 51);
		const motiveEnergySiteId = isProduction ? 532 : (isStaging ? 278 : 79);

		return {
			motiveEnergyEnterprise: motiveEnergyEnterprise,
			motiveEnergyCustomer: motiveEnergyCustomerId,
			motiveEnergySite: motiveEnergySiteId
		};
	}

	getCBLCustomerData() {
		return {
			enterprise_id: this.CBL_ENTERPRISE_ID,
			customer_id: this.CBL_CUSTOMER_ID,
			site_id: this.CBL_INVENTORY_SITE_ID,
		};
	}

	mapCellularRssi(cellular_rssi: number) {
		switch (true) {
			case cellular_rssi == 0:
				return this.translate.instant('cellular_rssi_values.zero_cellular_rssi');
			case cellular_rssi == 1:
				return this.translate.instant('cellular_rssi_values.one_cellular_rssi');
			case cellular_rssi >= 2 && cellular_rssi <= 30:
				return ((Math.abs(cellular_rssi) - 30) * 2 - 53) + " dBm";
			case cellular_rssi == 31:
				return this.translate.instant('cellular_rssi_values.mid_cellular_rssi');
			case cellular_rssi > 31 && cellular_rssi <= 98:
				return this.translate.instant('cellular_rssi_values.unknown_cellular_rssi', { cellular_rssi });
			case cellular_rssi == 99:
				return this.translate.instant('cellular_rssi_values.cellular_rssi_unknown_or_undetectable');
			default:
				break;
		}
	}

	adjustMacSearchString(searchString: any) {
		const obj = {
			mac_address: '',
			serial_number: '',
		};

		let isValidMac = this.validationService.macRegex.test(searchString);
		if (isValidMac) {
			obj.mac_address = searchString;
			return obj;
		}

		try {
			searchString = JSON.parse(searchString);
			obj.mac_address = searchString.M || searchString.m || '';
			const serialNumberKey = Object.keys(searchString).find(key => key.toLowerCase() === 'sn'); // support the keys [sn, Sn, sN, SN]
			obj.serial_number = serialNumberKey ? searchString[serialNumberKey] || '' : '';
			return obj;
		} catch (ex) {
			if (typeof searchString == 'string' && searchString.length == 24) {
				const mac = searchString.slice(0, 12);
				const sn = searchString.slice(12, 24);
				isValidMac = this.validationService.macRegex.test(mac);
				if (isValidMac) {
					obj.mac_address = mac;
					obj.serial_number = sn;
					return obj;
				} else if (this.validationService.macRegex.test(sn)) {
					obj.mac_address = sn;
					obj.serial_number = mac;
					return obj;
				}
			}

			return searchString;
		}
	}

	asciiValue (str: string) {
		let sum = 0;
		for (let i = 0; i < str.length; i++) {
			sum += str.charCodeAt(i);
		}
		return sum;
	}

	sortDataAlphabetically(data: string[], fieldName: string) {
		return data.sort((item1, item2) => item1[fieldName].toLowerCase() > item2[fieldName].toLowerCase() ? 1 : -1);
	}

	handleLinkClickEvents(event) {
		// Allow Ctrl+Click, Cmd+Click (for macOS), or middle-click to open in a new tab
		if (event.ctrlKey || event.metaKey || event.button === 1) {
			return; // Do nothing, allow the browser to handle it
		}

		// For normal left-clicks, prevent the default behavior to avoid page reload
		event.preventDefault();
	}

	scrollToActiveLink(cardContainer: ElementRef) {
		if (!cardContainer) return false;

		const container = cardContainer.nativeElement; // Access the DOM element
		const activeLink = container.querySelector('.active');

		if (!activeLink) return false;

		const containerRect = container.getBoundingClientRect();
		const activeLinkRect = activeLink.getBoundingClientRect();

		// Calculate the horizontal offset
		const offset = activeLinkRect.left - containerRect.left + container.scrollLeft;

		// Scroll the container horizontally to center the active link
		container.scrollTo({ left: offset - (container.clientWidth / 2), behavior: 'smooth' });

		return true;
	}

	// return true if the date is less than one day form 1970-01-01
	isUnixZero(dateStr: string): boolean {
		const timestamp = new Date(dateStr).getTime();
		const oneDay = 24 * 60 * 60 * 1000; // milliseconds in a day
		return timestamp <= oneDay || isNaN(timestamp);
	}

	getLastWeekDays(installationDate = 0) {
		let daysDifference = 0;
		if (new Date().getDay() === 0) {
			daysDifference = 7;
		}

		let startDay	= moment().day(-6-daysDifference).utc().startOf('day').unix();	// last Monday before Sunday

		if (installationDate > startDay)
			startDay = installationDate;

		let endDay		= moment().day(0-daysDifference).utc().startOf('day').unix();	// last Sunday

		let oneDaySeconds	= 60*60*24;
		let days = [];
		for(let i = startDay; i <= endDay; i += oneDaySeconds) {
			days.push(i);
		}
		return days;
	}

	getLastXDays(count: number, installationDate = 0) {
		let generateToDate	= moment().subtract(1, 'days').utc().startOf('day').unix();
		let days = [];
		let oneDaySeconds	= 60*60*24;
		for(let i = 0; i < count; i++) {
			const day = generateToDate-(i*oneDaySeconds);
			days.push(generateToDate-(i*oneDaySeconds));

			if (installationDate >= day)
				break;
		}
		return days;
	}

	getLastMonthDays(installationDate = 0) {
		let startDay	= moment().utc().subtract(1, 'months').startOf('month').unix();

		if (installationDate > startDay)
			startDay = installationDate;

		let endDay		= moment().utc().subtract(1, 'months').endOf('month').startOf('day').unix();
		let days = [];
		let oneDaySeconds	= 60*60*24;
		for(let i = startDay; i <= endDay; i += oneDaySeconds) {
			days.push(i);
		}
		return days;
	}

	getYearToDateDays(installationDate = 0) {
		let startDay	= moment().utc().startOf('year').unix();

		if (installationDate > startDay)
			startDay = installationDate;

		let endDay		= moment().subtract(1, 'days').utc().startOf('day').unix();
		let days = [];
		let oneDaySeconds	= 60*60*24;
		for(let i = startDay; i <= endDay; i += oneDaySeconds) {
			days.push(i);
		}
		return days;
	}

	getLastYearDays(installationDate = 0) {
		let startDay	= moment().subtract(1, 'year').utc().startOf('year').unix();

		if (installationDate > startDay)
			startDay = installationDate;

		let endDay		= moment().subtract(1, 'year').utc().endOf('year').startOf('day').unix();
		let days = [];
		let oneDaySeconds	= 60*60*24;
		for(let i = startDay; i <= endDay; i += oneDaySeconds) {
			days.push(i);
		}
		return days;
	}

	deg2rad(deg) {
		return deg * (Math.PI/180);
	}

	getDistanceFromLatLon(lat1, lon1, lat2, lon2, unit: 'feet' | 'km' = 'km') {
		const R = 6378.137; // Radius of the earth in km
		const dLat = this.deg2rad(lat2-lat1);  // deg2rad below
		const dLon = this.deg2rad(lon2-lon1);
		const a =
			Math.sin(dLat/2) * Math.sin(dLat/2) +
			Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) *
			Math.sin(dLon/2) * Math.sin(dLon/2)
		;
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
		let distance = R * c; // Distance in km

		if (unit === 'feet')
			distance = distance * 3280.84; // Convert km to feet

		return distance;
	}


	formatPopupTime(totalSeconds: number, period) {
		let periodTooltipValue = period;
		if (typeof period == 'object')
			periodTooltipValue = moment(period).format("MMM DD, YYYY");

		var hours = Math.round(totalSeconds / 3600);
		return `Period: ${periodTooltipValue}, ` + `Value: ` + hours + ` hours`;
	}

	formatPopupAh(value: number, period) {
		value = Math.round(value || 0);
		return `Period: ${period}, ` + `Value: ` + value + ` Ah`;
	}

	sortOptionsWithAllFirst(options: { value: any }[]): void {
		options.sort((a, b) => {
			if (a.value === 'all') {
				return -1;
			} else if (b.value === 'all') {
				return 1;
			} else {
				return a.value - b.value;
			}
		});
	}

	prepareOptions( values: number[]): {value: any, lable: string}[] {
		const returnArray = [];
		returnArray.push({ value: 'all', label: this.translate.instant('g.all') });

		const existValues = new Set<number>();

		for (const val of values || []) {
			if (existValues.has(val))
				continue;

			returnArray.push({ value: val, label: val });
			existValues.add(val);
		}

		this.sortOptionsWithAllFirst(returnArray);

		return returnArray;
	}

	getOptionLabel(items: { value?: any, id?: any, label?: any, text?: any, durations?: any, year?: any }[], value: any,
			opts: { valueKey?: string, labelKey?: string, fallbackKey?: string } = {}): string {
		const valueKey = opts.valueKey || (items?.[0]?.id !== undefined ? 'id' : 'value');
		const labelKey = opts.labelKey || (items?.[0]?.label !== undefined ? 'label' :
			items?.[0]?.text !== undefined ? 'text' :
			items?.[0]?.durations !== undefined ? 'durations' :
			items?.[0]?.year !== undefined ? 'year' : 'label');
		const fallbackKey = opts.fallbackKey || 'g.all';

		// '' is a valid sentinel value in several option lists (e.g. {id: '', text: 'All Truck Types'}),
		// so only null/undefined mean nothing was selected - '' must still resolve through the lookup below.
		const values = (Array.isArray(value) ? value : [value]).filter(val => val !== null && val !== undefined);
		if (!values.length)
			return this.translate.instant(fallbackKey);

		const labels = values.map(val => {
			const item = (items || []).find(i => i[valueKey] == val);
			return item ? item[labelKey] : val;
		});

		return labels.join(', ');
	}

	getTotalDaysInPeriod(datePeriod, period) {
		let totalDaysInPeriod = 0;

		switch (period) {
			case 'months':
				totalDaysInPeriod = this.getYearMonthDays(datePeriod);
				break;
			case 'quarters':
				totalDaysInPeriod = this.getYearQuarterDays(datePeriod);
				break;
			case 'years':
				const year = +datePeriod;
				totalDaysInPeriod = moment([year]).isLeapYear() ? 366 : 365;
				break;
			default:
				break;
		}

		return totalDaysInPeriod;
	}

	getYearMonthDays(yearMonth) {
		const [year, month] = yearMonth.split('_').map(Number);
		const now = moment();

		if (now.year() === year && now.month() === month - 1) 
			return now.date() - 1;

		return moment([year, month - 1]).daysInMonth();
	}

	getYearQuarterDays(yearQuarter) {
		const [yearStr, quarterStr] = yearQuarter.split('_');
		const year = parseInt(yearStr, 10);
		const quarter = parseInt(quarterStr.replace('Q', ''), 10);

		const startMonth = (quarter - 1) * 3;
		let totalDays = 0;

		for (let i = 0; i < 3; i++) {
			totalDays += moment([year, startMonth + i]).daysInMonth();
		}

		return totalDays;
	}

	setPageTabTitle(title: string) {
		return this.pageTitleService.setTitle(title);
	}

	getCsvFileHeadersInfo(config: { customerName?: string, siteName?: string, deviceSN?: string, includeSpacing?: boolean, poNumber?: string, trackingNumber?: string, shippingDate?: string , showTrackingNumber?: boolean }):  string[][] {
		const { customerName, siteName, deviceSN, includeSpacing = true, poNumber, trackingNumber, shippingDate, showTrackingNumber } = config;
		const rows = [[this.translate.instant('g.export_time'), moment().format('YYYY-MM-DD HH:mm:ss [UTC]Z')]];

		if (customerName)
			rows.push([this.translate.instant('g.customer_name'), customerName]);

		if (siteName)
			rows.push([this.translate.instant('g.site_name'), siteName]);

		if (deviceSN)
			rows.push([this.translate.instant('devices.device_sn'), deviceSN]);

		if (poNumber)
			rows.push([this.translate.instant('shipments_history.purchase_order'), poNumber]);
		
		if (showTrackingNumber)
			rows.push([this.translate.instant('orders.tracking_number'), `\t${trackingNumber ?? 'N/A'}`]);
		
		if (shippingDate)
			rows.push([this.translate.instant('shipments_history.shipment_date'), shippingDate]);

		if (includeSpacing)
			rows.push([], []);

		return rows;
	}

	isFWSupportField(field: string, FwVersion: number) {
		switch(field) {
			case 'max_voltage':
			case 'hot_disconnection':
			case 'zone_change':
				return FwVersion >= 2.4600;
			case 'disable_soc':
				return FwVersion >= 2.47;
			default:
				return false;
		}
	}

	getDeviceConnectivityAndShipmentStatus (device, field) {
		const shipmentResults = {
			notTested: 0,
			passed: 1,
			failed: 2
		}

		let res = 'not_tested';
		if (device[field] && device[field].report_id){
			if (device[field]?.result == shipmentResults.passed) {
				res = `passed`;
				if (device[field]?.skip_cellular_check)
					res = `passed_without_checking_cellular`;
			}

			if (device[field]?.result == shipmentResults.failed)
				res = `failed`;
		}

		return res;
	}
	
	formatTime(last_connect_time: string | number | Date) {
		const fromDate = Math.round(new Date(new Date((+last_connect_time) * 1000).getTime()).getTime());
		return moment(new Date(fromDate)).format('MM/DD/YYYY hh:mm:ss A');
	}
		
	getLastConnectedIn(last_connect_time: any) {
		const formattedLastConnectTime = this.formatTime(last_connect_time);
		const currentTime = moment();
		const lastConnectTime = moment(formattedLastConnectTime, 'MM/DD/YYYY hh:mm:ss A');
		const duration = moment.duration(currentTime.diff(lastConnectTime));
		if (duration.asMinutes() < 1)
			return this.translate.instant('devices.connected_less_than_one_minute');

		const days = Math.floor(duration.asDays()) ? `${Math.floor(duration.asDays())} Days` : "";
		const hours = duration.hours() ? `${duration.hours()} Hours` : "";
		const minute = duration.minutes() ? `${duration.minutes()} Minutes` : "" ;
		const result = days != "" ? `${days} ${hours}` : `${hours} ${minute}`;
		return result;
	}

	roundFloat(number: number, floatDigits = 0) {
		return Math.round(number * Math.pow(10, floatDigits)) / Math.pow(10, floatDigits);
	}

	calcVoltageReference(value: number, type: 'divide' | 'multi') {
		if (type === 'multi')
			return value * Math.sqrt(3);

		if (type === 'divide')
			return value / Math.sqrt(3);

		return value;
	}

	getFormattedDuration(durationSec: number, oneDay: boolean = false) {
		durationSec = Number(durationSec);
		const DD = Math.floor(durationSec / (24 * 60 * 60));
		durationSec -= DD * (24 * 60 * 60);
		const h = Math.floor(durationSec / 3600);
		const m = Math.floor((durationSec % 3600) / 60);
		const s = Math.floor((durationSec % 3600) % 60);
		if (oneDay)
			return h + 'H ' + m + 'M ' + s + 'S';
		return (DD > 0 ? DD + 'D ' : '') + h + 'H ' + m + 'M ' + s + 'S';
	}

	convertSecondsToHoursMinsSecs(totalSeconds: number) {
		if (!totalSeconds)
			return `00:00:00`;

		const totalMinutes = Math.floor(totalSeconds / 60);
		const seconds = totalSeconds % 60;
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;
		return `${hours > 10 ? hours : `0${hours}`}:${minutes > 10 ? minutes : `0${minutes}`}:${seconds > 10 ? seconds : `0${seconds}`}`;
	}

	getDeviceTabs(device: any, tabId: string, customerId) {
		const deviceTabs = DeviceFactory.createDevice(device.sct_device_type).config.deviceSettingTabs;
		// If tabId does not exist in tabs → return ['info']
		if (tabId && !deviceTabs.includes(tabId)) {
			this.router.navigate([customerId, device.site_id, device.mac_address, 'settings'],{ queryParams: { tab: 'info' } });
			return;
		}

		return tabId;
	}

	getDaysInQuarter(year: number, quarter: number) {
		return moment.utc(year, 'YYYY').quarter(quarter).endOf('quarter').diff(
			moment.utc(year, 'YYYY').quarter(quarter).startOf('quarter'), 'days'
		) + 1;
	}

	handleBatteryDeviceTempPerSiteSettingsUnit(temperature, unit) {
		if (unit) // CEL
			return temperature;
		return ((temperature - 32) * 5) / 9;// Feh
	}

	getDateRange(period: string, oldestInstallationDate?: number, validateInstallationDate: boolean = false): { from: Date | null; to: Date | null } {
		const today = moment();
		const yesterday = moment().subtract(1, 'day').endOf('day');
		let from = moment();
		let to = moment();

		const installationDate = oldestInstallationDate ? moment.unix(oldestInstallationDate) : null;

		switch (period) {
			case 'last_week': {
				const currentMonday = moment().startOf('isoWeek');
				from = currentMonday.clone().subtract(1, 'week');
				to = currentMonday.clone().subtract(1, 'millisecond');
				break;
			}

			case 'last_7_days':
				from = today.clone().subtract(7, 'days');
				to = yesterday.clone();
				break;

			case 'last_14_days':
				from = today.clone().subtract(14, 'days');
				to = yesterday.clone();
				break;

			case 'last_30_days':
				from = today.clone().subtract(30, 'days');
				to = yesterday.clone();
				break;

			case 'last_90_days':
				from = today.clone().subtract(90, 'days');
				to = yesterday.clone();
				break;

			case 'year_to_date':
				from = today.clone().startOf('year');
				to = yesterday.clone();
				break;

			case 'last_year':
				from = today.clone().subtract(1, 'year').startOf('year');
				to = today.clone().subtract(1, 'year').endOf('year');
				break;

			case 'since_installation':
				if (installationDate) {
					from = installationDate.clone();
				}

				to = yesterday.clone();
				break;
		}

		if (installationDate && from.isBefore(installationDate)) {
			from = installationDate.clone();
		}

		if (installationDate && installationDate.isAfter(to) && validateInstallationDate) {
			return { from: null, to: null };
		}

		from = from.startOf('day');
		to = to.endOf('day');

		return { from: from.toDate(), to: to.toDate() };
	}

	isWorkingDay(time: number, siteWD: number[]) {
		const weekday = moment(time * 1000).utc().isoWeekday() - 1;
		return siteWD.includes(weekday);
	};

	getManufacturingYear(value: string | number): number | null {
		if (!value) 
			return null;

		const str = value.toString().padStart(4, '0');

		const month = Number(str.slice(0, -2));
		const year = Number(str.slice(-2));

		if (month < 1 || month > 12)
			return null;

		return 2000 + year;
	}

	/**
	 * Formats a number for display with a comma as the thousands separator, e.g. 18430.6 -> '18,430.6'.
	 * The locale is pinned to en-US so the separator stays a comma whatever the browser language is.
	 */
	formatNumber(value: number, fractionDigits = 0): string {
		return (Number(value) || 0).toLocaleString('en-US', {
			minimumFractionDigits: fractionDigits,
			maximumFractionDigits: fractionDigits
		});
	}

	/**
	 * Expands one shortened device log line, e.g. 'SI:MN38:2,15' ->
	 * 'System-Info: Device Type: Battlink (2); Time zone: 15 (UTC-05:00) Eastern Time (US & Canada)'.
	 * A debug record carries the ring log's own '(<ticks>)->' stamp in front of the line; the
	 * decoder keeps it and expands what follows. A line whose code is not in the generated
	 * tables is returned exactly as given.
	 */
	decodeLogLine(line: string): string {
		return decodeLogLine(line);
	}

	scrollToWidget(widgetName: string) {
		const el = document.getElementById(`widget-${widgetName}`);
		if (!el) 
			return;

		const top = el.getBoundingClientRect().top + window.scrollY - 60;
		window.scrollTo({ top, behavior: 'smooth' });

		el.classList.add('widget-scroll-highlight');
		setTimeout(() => 
			el.classList.remove('widget-scroll-highlight')
		, 1500);
	}
}
